import { clearCachedJSON, getCachedETag, readCachedJSON, writeCachedJSON } from './api-cache';
import { buildBackendUrl } from "@/shared/api/backend";
import { sortAfdelingen } from "@/shared/utils/afdeling";
import { markActivityPulse } from "@/shared/utils/activity";
import { debugLog } from "@/shared/utils/debug-log";
import { dataError, dataLoading, dataReady, appHealth } from "@/app/state/app-state";
import { bedsState } from "@/pages/beds/state/beds-data";
import { patientsState } from "@/pages/patients/state/patients-data";
import { hapState } from "@/pages/hap/state/hap-state";
import { ovdsState, usersState } from "@/pages/administration/state/administration-state";

export type UserRole = "Administrator" | "Manager" | "HAP" | "Handover";

export type PublicUser = {
    handover_department_id?: string;
    id: string;
    name: string;
    username: string;
    role: UserRole;
    telefonist_enabled?: boolean;
    version?: number;
};

export type ReportUserOption = {
    id: string;
    name: string;
};

export type StatusReportSnapshot = {
    version: number;
    period: string;
    captured_at: string;
    beds: unknown[];
    patients: Record<string, unknown>;
    ovds: OVD[];
    hap: HAPDocument;
};

export type ShiftDefinition = {
    id: string;
    label: string;
    start: string;
    end: string;
};

export type ShiftSchedule = {
    version: number;
    normal_margin_minutes: number;
    maximum_margin_minutes: number;
    weekday_shifts: ShiftDefinition[];
    weekend_shifts: ShiftDefinition[];
};

export type ShiftInstance = {
    id: string;
    service_date: string;
    shift_id: string;
    label: string;
    official_start: string;
    official_end: string;
    flexible_start: string;
    flexible_end: string;
    maximum_start: string;
    maximum_end: string;
};

export type ShiftRecommendation = {
    shift_instance_id: string;
    shift_id: string;
    label: string;
    confidence: "high" | "medium" | "ambiguous" | "insufficient" | "manual";
    reason: string;
    official_minutes: number;
    flexible_minutes: number;
    activity_start?: string;
    activity_end?: string;
};

export type UserActivityBlock = {
    start_at: string;
    end_at: string;
    active_minutes: number;
    signals: string[];
    categories?: string[];
    incidental: boolean;
};

export type ReportShiftContext = {
    period: string;
    now: string;
    schedule: ShiftSchedule;
    instances: ShiftInstance[];
    recommendation?: ShiftRecommendation;
    activity_blocks: UserActivityBlock[];
};

export type AdminShiftActivityUser = {
    user_id: string;
    user_name: string;
    recommendation?: ShiftRecommendation;
    events: Array<{ id: string; signal: string; category?: string; created_at: string }>;
    activity_blocks: UserActivityBlock[];
};

export type ReportAttribution = {
    id: string;
    period: string;
    shift_instance_id: string;
    shift_id: string;
    shift_label: string;
    service_date: string;
    official_start: string;
    official_end: string;
    generated_at: string;
    generated_by_id: string;
    generated_by_name: string;
    confidence: string;
    selection_source: string;
    override_reason?: string;
};

export type PublicStatusReportAfdeling = {
    id: string;
    legacy_id?: string;
    building: string;
    floor: string;
    wing?: string;
    name: string;
};

export type PublicStatusReport = {
    historical?: boolean;
    evaluated_at?: string;
    bed_board?: PublicBedBoard;
    period: string;
    generated_at: string;
    afdeling: PublicStatusReportAfdeling;
    beds: unknown[];
    patients: Record<string, unknown>;
    ovds: Array<Pick<OVD, "id" | "building" | "floor" | "wing" | "name">>;
    ovd_status?: string;
};

export type PublicTelefonistManager = {
    availability_start?: string;
    availability_end?: string;
    name: string;
    available: boolean;
};

export type PublicBedBoardEntry = {
    bed_id: string;
    bed: {
        id: string;
        building: string;
        floor: string;
        room: string;
        bed: string;
        occupancy: string;
        has_active_patient: boolean;
        tags?: string[];
    };
};

export type PublicBedBoard = {
    generated_at: string;
    beds: PublicBedBoardEntry[];
};

export type PublicBedOccupancy = "Male" | "Female" | "Available" | "Blocked";
export type PublicPatientDischargeReason = "Deceased" | "Against Medical Advice (AMA)";

export type OVD = {
    id: string;
    building: string;
    floor: string;
    wing?: string;
    name: string;
    extension: string;
    version?: number;
};

export type HAPDayEntry = {
    version?: number;
    number: string;
    doctors: string;
    last_submitted_at?: string;
    last_submitted_shift?: string;
};

export type HAPEntryRecord = {
    id: string;
    year: number;
    month: number;
    day: number;
    name: string;
    number?: string;
    doctors?: string;
    submitted_at: string;
    submitted_shift?: string;
    entry_link_id?: string;
};

export type HAPEntryLink = {
    id: string;
    year: number;
    created_at: string;
    expires_at: string;
    revoked_at?: string;
};

export type HAPEntryLinkResult = HAPEntryLink & { token: string };

export type HAPYear = {
    version?: number;
    doctor_options: string[];
    schedule: HAPDayEntry[][];
};

export type HAPDocument = {
    version?: number;
    years: Record<string, HAPYear>;
    status_report_commentary?: Record<string, StatusReportCommentary>;
    entry_links?: HAPEntryLink[];
    entry_history?: HAPEntryRecord[];
};

export type HAPEntryBootstrap = {
    year: number;
    doctor_options: string[];
};

export type HandoverFieldDefinition = {
    id: string;
    label: string;
    type: "number" | "text";
};

export type HandoverRoomSlotDefinition = {
    id: string;
    label: string;
};

export type HandoverRoomGroupDefinition = {
    id: string;
    label: string;
    slots: HandoverRoomSlotDefinition[];
};

export type HandoverFormDefinition = {
    id: string;
    label: string;
    version: number;
    rooms: HandoverRoomGroupDefinition[];
    fields: HandoverFieldDefinition[];
};

export type HandoverStatus = "new" | "read" | "completed";

export type HandoverSubmissionRequest = {
    department_id: string;
    form_version: number;
    oldest_on_duty: string;
    room_notes: Record<string, string>;
    answers: Record<string, string>;
    general_notes: string;
};

export type HandoverSubmission = HandoverSubmissionRequest & {
    archived?: boolean;
    archived_at?: string;
    archived_by?: string;
    id: string;
    revision: number;
    department_label: string;
    status: HandoverStatus;
    service_date: string;
    shift_instance_id?: string;
    shift_label?: string;
    submitted_at: string;
    updated_at: string;
    edit_expires_at: string;
    read_at?: string;
    completed_at?: string;
};

export type HandoverAccess = {
    submission: HandoverSubmission;
    edit_token?: string;
    editable: boolean;
    locked_reason?: "read" | "expired" | "unavailable" | string;
};

export type HandoverSummaryDepartment = {
    department_id: string;
    department_label: string;
    received: boolean;
    submission_id?: string;
    status?: HandoverStatus;
    oldest_on_duty?: string;
    submitted_at?: string;
    answers?: Record<string, string>;
};

export type HandoverSummary = {
    service_date: string;
    shift_instance_id?: string;
    shift_label?: string;
    expected_departments: number;
    received_departments: number;
    new_count: number;
    departments: HandoverSummaryDepartment[];
};

export type HAPEntrySubmission = {
    name: string;
    year: number;
    month: number;
    day: number;
    number?: string;
    doctors?: string;
    submitted_at: string;
    submitted_shift?: string;
};

export type StatusReportCommentary = {
    ovd_statuses?: Record<string, string>;
    seh_status?: string;
    dialyse_status?: string;
    commentary?: string;
};

export type SyncActivityPoint = {
    version?: number;
    t: number;
    ts: string;
    tx: number;
    rx: number;
};

export type SyncActivitySnapshot = {
    version?: number;
    range: string;
    bucket_seconds: number;
    tx_per_sec: number;
    rx_per_sec: number;
    points: SyncActivityPoint[];
};

export type LatencyHistoryPoint = {
    version?: number;
    t: number;
    ts: string;
    http_ms?: number;
    auth_ms?: number;
};

export type LatencyHistorySnapshot = {
    version?: number;
    range: string;
    bucket_seconds: number;
    points: LatencyHistoryPoint[];
};

export type UsageAnalyticsEvent = {
    type: "page_visit" | "page_time" | "click" | "dead_click" | "rage_click" | "near_miss";
    page: string;
    session_id: string;
    visible_ms?: number;
    engaged_ms?: number;
    target_id?: string;
    target_kind?: "button" | "link" | "control" | "row" | "other";
    viewport?: "small" | "medium" | "large";
    x?: number;
    y?: number;
    target_width?: "small" | "medium" | "large";
    target_height?: "small" | "medium" | "large";
};

export type UsageAnalyticsBatch = {
    batch_id: string;
    events: UsageAnalyticsEvent[];
};

export type UsageAnalyticsSummary = {
    visits: number;
    unique_users: number;
    visible_ms: number;
    engaged_ms: number;
    clicks: number;
    dead_clicks: number;
    rage_clicks: number;
    near_misses: number;
};

export type UsageAnalyticsSnapshot = {
    enabled: boolean;
    retention_days: number;
    from: string;
    to: string;
    totals: UsageAnalyticsSummary;
    pages: Array<UsageAnalyticsSummary & { page: string }>;
    targets: Array<{
        page: string;
        target_id: string;
        target_kind: string;
        viewport: string;
        target_width?: string;
        target_height?: string;
        clicks: number;
        rage_clicks: number;
        near_misses: number;
    }>;
    heatmap: Array<{ page: string; viewport: string; x: number; y: number; clicks: number; dead_clicks: number }>;
    users: Array<{ id: string; name: string }>;
};

export type BackupMetadata = {
    branch?: string;
    format?: 'full' | 'delta';
    base?: string;
    parent?: string;
    source?: string;
    checksum?: string;
    version?: number;
    name: string;
    size_bytes: number;
    updated_at: string;
    kind?: string;
    triggered_by?: string;
    month?: string;
    requires_extraction?: boolean;
    archive_name?: string;
    archive_member_name?: string;
    physical?: boolean;
};

export type ServerLogsResponse = {
    lines?: string[];
    updated_at?: string;
};

export type HealthResponse = {
    status?: string;
    version?: string;
    build?: string;
};

// Compatibility exports keep existing feature imports stable while state ownership
// moves into app and feature state modules.
export { dataError, dataLoading, dataReady, appHealth } from "@/app/state/app-state";
export { bedsState } from "@/pages/beds/state/beds-data";
export { patientsState } from "@/pages/patients/state/patients-data";
export { hapState } from "@/pages/hap/state/hap-state";
export { ovdsState, usersState } from "@/pages/administration/state/administration-state";

type UsersResponse = {
    users?: PublicUser[];
};

type BackupsResponse = {
    backups?: BackupMetadata[];
    total?: number;
};

const appDataCacheKeys = ["beds", "patients", "ovds", "hap", "users"] as const;
let unauthorizedHandler: (() => void) | null = null;
export type SessionRenewalOutcome =
    | { kind: "renewed"; user: { id: string; name: string; role: string } }
    | { kind: "invalid" }
    | { kind: "unavailable"; reason: string };

let sessionRenewalHandler: (() => Promise<SessionRenewalOutcome>) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
	unauthorizedHandler = handler;
}

export function setSessionRenewalHandler(handler: () => Promise<SessionRenewalOutcome>) {
    sessionRenewalHandler = handler;
}

async function fetchWithCredentials(path: string, init?: RequestInit) {
    return fetch(buildBackendUrl(path), {
        credentials: "include",
        ...init,
    });
}

async function readErrorResponse(response: Response) {
	if (response.status === 401) {
		return "Session expired.";
	}

	const contentType = response.headers.get("content-type") ?? "";
	if (contentType.includes("application/json")) {
		const payload = (await response.json()) as { error?: string };
		return payload.error?.trim() || `Request failed: ${response.status}`;
	}

	const message = (await response.text()).trim();
	return message || `Request failed: ${response.status}`;
}

async function fetchAuthenticated(path: string, init?: RequestInit) {
    let response = await fetchWithCredentials(path, init);

    if (response.status !== 401 || !sessionRenewalHandler || path.startsWith("/auth/")) {
        return response;
    }

    let outcome: SessionRenewalOutcome;
    try {
        outcome = await sessionRenewalHandler();
    } catch {
        throw new Error("Backend unavailable.");
    }

    if (outcome.kind === "renewed") {
        response = await fetchWithCredentials(path, init);
    } else if (outcome.kind === "invalid") {
        unauthorizedHandler?.();
    } else {
        throw new Error("Backend unavailable.");
    }

    return response;
}

async function request(path: string, init?: RequestInit) {
    markActivityPulse();
    const response = await fetchAuthenticated(path, init);

    if (!response.ok) {
		throw new Error(await readErrorResponse(response));
    }

    return response;
}

async function saveCachedJSON<T>(path: string, cacheKey: string, value: T) {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const etag = getCachedETag(cacheKey);
    if (etag) {
        headers.set('If-Match', etag);
    }

    const response = await request(path, {
        method: 'PUT',
        headers,
        body: JSON.stringify(value),
    });
    writeCachedJSON(cacheKey, value, response.headers.get('etag'));
}

async function loadCachedJSON<T>(path: string, cacheKey: string) {
    const cached = readCachedJSON<T>(cacheKey);
    const headers = new Headers();
    let hasValidator = false;
    if (cached?.etag) {
        headers.set("If-None-Match", cached.etag);
        hasValidator = true;
        debugLog("api/cache", "validator-sent", { cacheKey, etag: cached.etag });
    }

    let response = await fetchAuthenticated(path, hasValidator ? { headers } : undefined);

    if (response.status === 304) {
        if (cached) {
            debugLog("api/cache", "cache-reused-304", { cacheKey });
            return cached.value;
        }
        debugLog("api/cache", "cache-304-without-body", { cacheKey });
        response = await fetchAuthenticated(path);
    }

    if (!response.ok) {
		throw new Error(await readErrorResponse(response));
    }

    const value = (await response.json()) as T;
    writeCachedJSON(cacheKey, value, response.headers.get("etag"));
    debugLog("api/cache", "cache-refreshed", { cacheKey });
    return value;
}

export async function loadBeds() {
    return loadCachedJSON<unknown[]>("/beds", "beds");
}

export async function saveBeds(payload: unknown) {
    console.debug("[debugLog]", "api/save-beds", { count: Array.isArray(payload) ? payload.length : null });
    await saveCachedJSON('/beds', 'beds', payload);
}

export async function loadPatients() {
    return loadCachedJSON<Record<string, unknown>>("/patients", "patients");
}

export async function savePatients(payload: unknown) {
    console.debug("[debugLog]", "api/save-patients", { keys: payload && typeof payload === "object" ? Object.keys(payload as Record<string, unknown>).length : null });
    await saveCachedJSON('/patients', 'patients', payload);
}

export async function saveBoard(beds: unknown, patients: unknown) {
    const response = await request('/board', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            beds,
            patients,
            beds_etag: getCachedETag('beds') ?? '',
            patients_etag: getCachedETag('patients') ?? '',
        }),
    });
    const etags = await response.json() as { beds_etag?: string; patients_etag?: string };
    writeCachedJSON('beds', beds, etags.beds_etag ?? null);
    writeCachedJSON('patients', patients, etags.patients_etag ?? null);
}

type AuditIntent = { type: string; changes: Array<{ field: string; from?: unknown; to?: unknown }> };

export async function patchBed(bedID: string, bedChanges: Record<string, unknown>, patientChanges?: Record<string, unknown>, bedEvents?: AuditIntent[], patientEvents?: AuditIntent[]) {
	const cachedBeds = readCachedJSON<Array<{ bed_id: string } & Record<string, unknown>>>('beds');
	const cachedPatients = readCachedJSON<Record<string, unknown>>('patients');
    const response = await request(`/beds/${encodeURIComponent(bedID)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            bed_changes: bedChanges,
            ...(patientChanges && Object.keys(patientChanges).length ? { patient_changes: patientChanges } : {}),
            ...(bedEvents?.length ? { bed_events: bedEvents } : {}),
            ...(patientEvents?.length ? { patient_events: patientEvents } : {}),
            beds_etag: getCachedETag('beds') ?? '',
            patients_etag: getCachedETag('patients') ?? '',
        }),
    });
    const result = await response.json() as { bed?: { bed_id: string } & Record<string, unknown>; patient?: unknown; bed_changed: boolean; patient_changed: boolean; beds_etag?: string; patients_etag?: string };
    const beds = result.bed && cachedBeds ? cachedBeds.value.map((entry) => entry.bed_id === result.bed?.bed_id ? result.bed : entry) : cachedBeds?.value;
    const patients = result.patient && cachedPatients ? { ...cachedPatients.value, [bedID]: result.patient } : cachedPatients?.value;
    if (!beds || !patients) throw new Error('Cached board data is unavailable; reload and retry.');
    writeCachedJSON('beds', beds, result.beds_etag ?? null);
    writeCachedJSON('patients', patients, result.patients_etag ?? null);
    return { beds, patients, bedChanged: result.bed_changed, patientChanged: result.patient_changed };
}

export async function loadOVDs() {
    return sortAfdelingen(await loadCachedJSON<OVD[]>("/ovds", "ovds"));
}

export async function saveOVDs(payload: OVD[]) {
    await saveCachedJSON('/ovds', 'ovds', payload);
}

export async function loadHAP() {
    return loadCachedJSON<HAPDocument>("/hap", "hap");
}

export async function saveHAP(payload: HAPDocument) {
    await saveCachedJSON('/hap', 'hap', payload);
}

export async function loadHAPOnly() {
    dataLoading.value = true;
    dataError.value = null;
    try {
        hapState.value = await loadHAP();
        dataReady.value = true;
    } catch (error) {
        dataError.value = error instanceof Error ? error.message : "Failed to load HAP data.";
        throw error;
    } finally {
        dataLoading.value = false;
    }
}

export type AppDataResource = "beds" | "patients" | "ovds" | "hap" | "users";

export async function reloadAppDataResources(resources: readonly AppDataResource[]) {
    const requested = new Set(resources);
    const [beds, patients, ovds, hap, users] = await Promise.all([
        requested.has("beds") ? loadBeds() : Promise.resolve(undefined),
        requested.has("patients") ? loadPatients() : Promise.resolve(undefined),
        requested.has("ovds") ? loadOVDs() : Promise.resolve(undefined),
        requested.has("hap") ? loadHAP() : Promise.resolve(undefined),
        requested.has("users") ? loadUsers() : Promise.resolve(undefined),
    ]);

    if (beds !== undefined) bedsState.value = beds;
    if (patients !== undefined) patientsState.value = patients;
    if (ovds !== undefined) ovdsState.value = ovds;
    if (hap !== undefined) hapState.value = hap;
    if (users !== undefined) usersState.value = users;
}

async function publicHAPEntryRequest<T>(init?: RequestInit) {
    const response = await fetch(buildBackendUrl('/public/hap/entry'), { ...init, credentials: 'include', cache: 'no-store' });
    if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || `Request failed: ${response.status}`);
    }
    return (await response.json()) as T;
}

export async function createHAPEntryLink() {
    const response = await request('/hap/entry-links', { method: 'POST' });
    return response.json() as Promise<HAPEntryLinkResult>;
}

export async function revokeHAPEntryLink(id: string) {
    await request(`/hap/entry-links/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

async function publicStatusReportRequest<T>(path: string, init?: RequestInit) {
    const response = await fetch(buildBackendUrl(path), { ...init, cache: "no-store", credentials: "include" });
    if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || `Request failed: ${response.status}`);
    }
    return (await response.json()) as T;
}

export async function loadPublicStatusReportAfdelingen() {
    const payload = await publicStatusReportRequest<{ afdelingen?: PublicStatusReportAfdeling[] }>("/public/status-report/afdelingen");
    return sortAfdelingen(Array.isArray(payload.afdelingen) ? payload.afdelingen : []);
}

export async function loadPublicStatusReportOptions(period = "", signal?: AbortSignal) {
    const payload = await publicStatusReportRequest<{ afdelingen?: PublicStatusReportAfdeling[]; periods?: string[]; current_period?: string }>(`/public/status-report/afdelingen${period ? `?period=${encodeURIComponent(period)}` : ""}`, { signal });
    return {
        afdelingen: sortAfdelingen(Array.isArray(payload.afdelingen) ? payload.afdelingen : []),
        periods: Array.isArray(payload.periods) ? payload.periods : [],
        current_period: typeof payload.current_period === "string" ? payload.current_period : "",
    };
}

export async function loadPublicStatusReport(afdelingID: string, signal?: AbortSignal, period = "") {
    return publicStatusReportRequest<PublicStatusReport>(`/public/status-report?afdeling_id=${encodeURIComponent(afdelingID)}${period ? `&period=${encodeURIComponent(period)}` : ""}`, { signal });
}

export async function loadPublicTelefonist(signal?: AbortSignal) {
    const payload = await publicStatusReportRequest<{ managers?: PublicTelefonistManager[] }>("/public/telefonist", { signal });
    return Array.isArray(payload.managers) ? payload.managers : [];
}

export async function loadPublicBedBoardAfdelingen() {
    const payload = await publicStatusReportRequest<{ afdelingen?: PublicStatusReportAfdeling[] }>('/public/bed-board/afdelingen');
    return sortAfdelingen(Array.isArray(payload.afdelingen) ? payload.afdelingen : []);
}

export async function loadPublicBedBoard(afdelingID: string, signal?: AbortSignal) {
    return publicStatusReportRequest<PublicBedBoard>(`/public/bed-board?afdeling_id=${encodeURIComponent(afdelingID)}`, { signal });
}

export async function updatePublicBedBoardOccupancy(bedID: string, occupancy: PublicBedOccupancy) {
    return publicStatusReportRequest<{ bed_id: string; occupancy: PublicBedOccupancy; updated_at: string; changed: boolean }>(`/public/bed-board/${encodeURIComponent(bedID)}/occupancy`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ occupancy }),
    });
}

export async function dischargePublicBedBoardPatient(bedID: string, reason: PublicPatientDischargeReason, deceasedAt: string, confirmation: string) {
    return publicStatusReportRequest<{ bed_id: string; reason: PublicPatientDischargeReason; updated_at: string; changed: boolean }>(`/public/bed-board/${encodeURIComponent(bedID)}/discharge`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, deceased_at: deceasedAt, confirmation }),
    });
}

export async function switchPublicBedBoardPatients(sourceBedID: string, targetBedID: string, afdelingID: string, confirmation: string) {
    return publicStatusReportRequest<{ source_bed_id: string; target_bed_id: string; updated_at: string; changed: boolean }>(`/public/bed-board/${encodeURIComponent(sourceBedID)}/switch`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_bed_id: targetBedID, afdeling_id: afdelingID, confirmation }),
    });
}

export async function loadHandoverDefinitions(signal?: AbortSignal) {
    const response = await request("/handover/definitions", { signal });
    const payload = (await response.json()) as { definitions?: HandoverFormDefinition[] };
    return Array.isArray(payload.definitions) ? payload.definitions : [];
}

export async function submitAuthenticatedHandover(payload: HandoverSubmissionRequest) {
    const response = await request("/handover", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    return await response.json() as HandoverAccess;
}

export async function loadHandoverInbox(filters: { serviceDate?: string; shiftInstanceID?: string; status?: HandoverStatus; archived?: boolean } = {}) {
    const query = new URLSearchParams();
    if (filters.serviceDate) query.set("service_date", filters.serviceDate);
    if (filters.shiftInstanceID) query.set("shift_instance_id", filters.shiftInstanceID);
    if (filters.status) query.set("status", filters.status);
    query.set("archived", String(filters.archived ?? false));
    const suffix = query.size ? `?${query.toString()}` : "";
    const response = await request(`/handover${suffix}`);
    const payload = (await response.json()) as { submissions?: HandoverSubmission[] };
    return Array.isArray(payload.submissions) ? payload.submissions : [];
}

export async function loadHandoverSummary() {
    return (await request("/handover/summary")).json() as Promise<HandoverSummary>;
}

export async function markHandoverRead(id: string) {
    const response = await request(`/handover/${encodeURIComponent(id)}/read`, { method: "POST" });
    return (await response.json()) as HandoverSubmission;
}

export async function completeHandover(id: string) {
    const response = await request(`/handover/${encodeURIComponent(id)}/complete`, { method: "POST" });
    return (await response.json()) as HandoverSubmission;
}

export async function archiveHandover(id: string) {
    return (await request(`/handover/${encodeURIComponent(id)}/archive`, { method: "POST" })).json() as Promise<HandoverSubmission>;
}

export async function restoreHandover(id: string) {
    return (await request(`/handover/${encodeURIComponent(id)}/restore`, { method: "POST" })).json() as Promise<HandoverSubmission>;
}

export function publicBedBoardStreamURL(afdelingID: string) {
    return buildBackendUrl(`/public/bed-board/stream?afdeling_id=${encodeURIComponent(afdelingID)}`);
}

export async function loadPublicHAPEntry() {
    return publicHAPEntryRequest<HAPEntryBootstrap>();
}

export async function submitPublicHAPEntry(payload: { name: string; number: string; doctors: string; submitted_shift: string }) {
    return publicHAPEntryRequest<HAPEntrySubmission>({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
}

export async function saveStatusReportCommentaryField(payload: { period: string; shiftInstanceID?: string; field: string; value: string; lockToken: string }) {
    const response = await request('/report/status-commentary', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period: payload.period, shift_instance_id: payload.shiftInstanceID, field: payload.field, value: payload.value, lock_token: payload.lockToken }) });
    return (await response.json()) as HAPDocument;
}

export async function loadReportShiftContext(period: string) {
    const response = await request(`/report/shift-context?period=${encodeURIComponent(period)}`);
    return (await response.json()) as ReportShiftContext;
}

export async function recordUserActivity(signal: string, category: string) {
    await request("/report/activity", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signal, category }),
    });
}

export async function saveReportShiftAttribution(payload: { period: string; shiftInstanceID: string; overrideReason?: string }) {
    const response = await request("/report/shift-attribution", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period: payload.period, shift_instance_id: payload.shiftInstanceID, override_reason: payload.overrideReason ?? "" }),
    });
    return (await response.json()) as ReportAttribution;
}

export async function loadAdminShiftActivity(period: string, userID: string) {
    const response = await request(`/admin/report/activity?period=${encodeURIComponent(period)}&user_id=${encodeURIComponent(userID)}`);
    return (await response.json()) as ReportShiftContext & { events: Array<{ id: string; signal: string; category?: string; created_at: string }>; user_timelines?: AdminShiftActivityUser[] };
}

export async function loadUsers() {
	const response = await fetchAuthenticated("/users");
	if (response.status === 403) {
		return [];
	}
	if (!response.ok) {
		throw new Error(await readErrorResponse(response));
	}

	const payload = (await response.json()) as UsersResponse;
	return Array.isArray(payload.users) ? payload.users : [];
}

export async function loadReportUserOptions() {
    const response = await fetchAuthenticated("/report/user-options");
    if (!response.ok) {
        throw new Error(await readErrorResponse(response));
    }

    const payload = (await response.json()) as { users?: ReportUserOption[] };
    return Array.isArray(payload.users) ? payload.users : [];
}

export async function loadStatusReportSnapshot(period: string) {
    const response = await request(`/report/status-snapshot?period=${encodeURIComponent(period)}`);
    return (await response.json()) as StatusReportSnapshot;
}

export async function createUser(payload: { name: string; username: string; role: string; password?: string; handover_department_id?: string }) {
    const response = await request("/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return (await response.json()) as { user?: PublicUser; temporary_password?: string };
}

export async function loadHandoverAccounts(signal?: AbortSignal) {
    const response = await request("/handover/accounts", { signal });
    const payload = (await response.json()) as { users?: PublicUser[] };
    return Array.isArray(payload.users) ? payload.users : [];
}

export async function createHandoverAccount(payload: { name: string; username: string; password?: string; handover_department_id: string }) {
    const response = await request("/handover/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return (await response.json()) as { user?: PublicUser; temporary_password?: string };
}

export async function updateHandoverAccount(payload: { id: string; name: string; username: string; password?: string; reset_password?: boolean; handover_department_id: string }) {
    const response = await request("/handover/accounts", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return (await response.json()) as { user?: PublicUser; temporary_password?: string };
}

export async function deleteHandoverAccount(id: string) {
    await request("/handover/accounts", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
    });
}

export async function updateUser(payload: { id: string; name: string; username: string; role: string; password?: string; reset_password?: boolean; telefonist_enabled?: boolean; handover_department_id?: string }) {
    const response = await request("/users", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return (await response.json()) as { user?: PublicUser; temporary_password?: string };
}

export async function deleteUser(id: string) {
    await request("/users", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
    });
}

export async function loadSyncActivity(range: string) {
    return (await request(`/metrics/sync-activity?range=${encodeURIComponent(range)}`)).json() as Promise<SyncActivitySnapshot>;
}

export async function loadLatencyHistory(range: string) {
    return (await request(`/metrics/latency?range=${encodeURIComponent(range)}`)).json() as Promise<LatencyHistorySnapshot>;
}

export async function recordLatencySample(kind: "http" | "auth", latency: number) {
    await request("/metrics/latency", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, latency }),
    });
}

export async function loadUsageAnalyticsConfig() {
    return (await request("/metrics/usage/config")).json() as Promise<{ enabled: boolean; retention_days: number }>;
}

export async function recordUsageAnalyticsBatch(batch: UsageAnalyticsBatch, keepalive = false) {
    const response = await fetchAuthenticated("/metrics/usage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
        keepalive,
    });
    if (!response.ok) throw new Error(`Usage analytics request failed: ${response.status}`);
}

export async function loadUsageAnalytics(filters: { from: string; to: string; page?: string; user?: string }) {
    const query = new URLSearchParams({ from: filters.from, to: filters.to });
    if (filters.page) query.set("page", filters.page);
    if (filters.user) query.set("user", filters.user);
    return (await request(`/admin/analytics?${query.toString()}`)).json() as Promise<UsageAnalyticsSnapshot>;
}

export async function clearUsageAnalytics() {
    await request("/admin/analytics", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: "DELETE" }),
    });
}

export type ProductionDataSyncStatus = {
    environment: string;
    can_sync: boolean;
};

export async function loadProductionDataSyncStatus() {
    return (await request("/admin/data-sync/status")).json() as Promise<ProductionDataSyncStatus>;
}

export async function syncProductionData() {
    return (await request("/admin/data-sync/production", { method: "POST" })).json() as Promise<{ environment: string; status: string }>;
}

export async function createBackup(branch = 'main') {
    const response = await request(`/admin/backup?branch=${encodeURIComponent(branch)}`, { method: "POST" });
    return (await response.json()) as { status?: "ok" | "skipped" | "skipped_no_changes"; created?: boolean; file_name?: string };
}

export async function downloadBackupSnapshot(name?: string) {
    const response = await request(name ? `/admin/backup?file_name=${encodeURIComponent(name)}` : '/admin/backup');
    const contentDisposition = response.headers.get("content-disposition") ?? "";
    const match = contentDisposition.match(/filename="([^"]+)"/i);
    return {
        blob: await response.blob(),
        fileName: match?.[1] || `blink-hoh-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`,
    };
}

export async function forkBackup(fileName: string, branch: string) {
    await request('/admin/backups/branch', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({file_name: fileName, branch})});
}

export async function loadBackupPolicy(): Promise<{automatic_enabled: boolean; branches: string[]}> {
    const response = await request('/admin/backups/policy');
    return response.json();
}

export async function loadBackups(limit = 100, offset = 0) {
    const response = await request(`/admin/backups?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}`);
    const payload = (await response.json()) as BackupsResponse;
    return {
        backups: Array.isArray(payload.backups) ? payload.backups : [],
        total: typeof payload.total === "number" ? payload.total : Array.isArray(payload.backups) ? payload.backups.length : 0,
    };
}

export async function loadBackupCount() {
	const response = await request("/admin/backups/count");
	const payload = (await response.json()) as { count?: number };
	return typeof payload.count === "number" ? payload.count : 0;
}

export async function deleteBackup(fileName: string) {
	await request("/admin/backup", {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ file_name: fileName }),
	});
}

export async function restoreBackup(fileName: string) {
    await request("/admin/restore-backup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_name: fileName }),
    });
}

export async function importBackup(file: File) {
    const form = new FormData();
    form.append("file", file);
    await request("/admin/import-backup", {
        method: "POST",
        body: form,
    });
}

export async function resetData(password: string) {
    await request("/admin/reset-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
    });
}

export async function loadServerLogs(limit = 300) {
    return (await request(`/admin/server-logs?limit=${encodeURIComponent(String(limit))}`)).json() as Promise<ServerLogsResponse>;
}

export async function loadHealth() {
    try {
        const response = await fetchWithCredentials("/health", { cache: "no-store" });
        if (!response.ok) {
            const contentType = response.headers.get("content-type") ?? "";
            if (contentType.includes("application/json")) {
                const payload = (await response.json()) as { error?: string };
                throw new Error(payload.error?.trim() || `Request failed: ${response.status}`);
            }

            const message = (await response.text()).trim();
            throw new Error(message || `Request failed: ${response.status}`);
        }
        const payload = (await response.json()) as HealthResponse;
        appHealth.value = {
            status: typeof payload.status === "string" ? payload.status : "ok",
            version: typeof payload.version === "string" ? payload.version : "",
            build: typeof payload.build === "string" ? payload.build : "",
        };
        return appHealth.value;
    } catch (error) {
        appHealth.value = { status: "error", version: "", build: "" };
        throw error;
    }
}

export async function loadAppData() {
    console.debug("[debugLog]", "api/load-app-data:start", { dataLoading: dataLoading.value, dataReady: dataReady.value });
    dataLoading.value = true;
    dataError.value = null;

    try {
        const [beds, patients, ovds, hap, users] = await Promise.all([
            loadBeds(),
            loadPatients(),
            loadOVDs(),
            loadHAP(),
            loadUsers(),
        ]);

        bedsState.value = beds;
        patientsState.value = patients;
        ovdsState.value = ovds;
        hapState.value = hap;
        usersState.value = users;
        dataReady.value = true;
        console.debug("[debugLog]", "api/load-app-data:done", { beds: beds.length, patients: Object.keys(patients).length, ovds: ovds.length, users: users.length });
    } catch (error) {
        dataError.value = error instanceof Error ? error.message : "Failed to load data.";
        console.debug("[debugLog]", "api/load-app-data:error", { message: dataError.value });
        throw error;
    } finally {
        dataLoading.value = false;
    }
}

export function clearAppData() {
    bedsState.value = [];
    patientsState.value = {};
    ovdsState.value = [];
    hapState.value = { years: {} };
    usersState.value = [];
    dataError.value = null;
    dataReady.value = false;
}

export function clearAppDataCache() {
    clearCachedJSON(appDataCacheKeys);
}
